import React, { ReactNode, useMemo } from 'react';
import { history, useIntl, getLocale } from 'umi';
import { Bubble, Tooltip } from 'tea-component';
import TipContext from '@/pages/home/components/TipContext';
import AggregationBubble from '@/pages/intelligence/pages/TopCharts/components/DetailsPage/AggregationBubble';
import { datamapIntelligenceMetricsPath } from '@/pages/intelligence/utils/common';
import { getCountryFlag } from '@/pages/home/_config/countries';
import { getDataSourceIcon } from '@/pages/intelligence/utils/getDataSourceImg';
import { DataSourceBubbleInfo } from '@/pages/intelligence/components/DataSourceComponents';
import {
  LANGUAGE_CN,
  sourceMap,
  // showTipList
} from '@intelligence/_config/config';
import SvgIcon from '@/pages/home/components/SvgIcon';

interface ICardTitleContent {
  titleIcon: string;
  titleContent: string;
  baseIndicators: any;
  data_source: string;
  reginonOnlyTips?: boolean;
  index?: number;
  moduleName?: string;
  entityType?: string;
  cardItem?: any;
  // 自定义展示气泡内容
  customBubbleContent: any;
  // 标题 hover 气泡宽度
  aggregationBubbleWidth?: number;
  isOperateRegion: boolean;
  isShowDataSourceIcon?: boolean; // 是否展示数据源icon
  metricTitle: string;
  metricTitleLocaleText: string;
  link_url?: string;
  isShowInfoIcon?: boolean;
  tipsInfoText?: ReactNode;
}

// overview卡片新组件
const CardTitleContent = (props: ICardTitleContent) => {
  const {
    titleIcon,
    titleContent: titleContentTemp,
    baseIndicators,
    data_source,
    reginonOnlyTips,
    index,
    moduleName,
    entityType,
    cardItem = {},
    customBubbleContent,
    isOperateRegion = false,
    isShowDataSourceIcon = true, // 是否展示数据源icon
    metricTitle = '',
    metricTitleLocaleText = '',
    link_url,
    aggregationBubbleWidth = 550,
    isShowInfoIcon = false,
    tipsInfoText,
  } = props;

  // console.log('CardTitleContent', props);

  const usMetricList = ['digitalARPPU', 'physicalRevenue'];

  const { formatMessage } = useIntl();
  const locale = getLocale();

  const getIsUsMetric = (cardItem: any) => {
    if (cardItem?.data_source != 'gsd') {
      if (!isOperateRegion && cardItem?.metricUserGuide == 'digitalRevenue') {
        return true;
      }
      if (usMetricList.includes(cardItem?.metricUserGuide)) {
        return true;
      }
    }
    return false;
  };

  const titleContent = useMemo(() => {
    if (getIsUsMetric(cardItem)) {
      return `US ${cardItem.titleContent}`;
    }
    return titleContentTemp;
  }, [titleContentTemp, cardItem]);

  const getCompanyPrefixOfDataSourceIconTips = () => {
    if (moduleName !== 'companies') return '';
    const companyText = formatMessage({ id: 'dbi_company' });
    return locale === LANGUAGE_CN ? companyText : `${companyText} `;
  };

  const renderDataSourceTips = (
    titleIcon: string,
    titleContent: string,
    data_source: string,
    metricTitleLocaleKey: string,
  ) => {
    const isPcuOrAcu = ['PCU', 'ACU'].includes(titleContent?.toUpperCase());
    return (
      <DataSourceBubbleInfo
        dataSource={titleIcon}
        dataOfDom={
          <>
            {isPcuOrAcu && (
              <img
                className="platform-title-icon"
                style={{
                  height: '16px',
                  width: '16px',
                  margin: '0 4px',
                  verticalAlign: 'middle',
                }}
                src={getDataSourceIcon(titleIcon)}
              />
            )}
            {getCompanyPrefixOfDataSourceIconTips()}
            {metricTitleLocaleKey}
          </>
        }
        comesFormDom={
          <>
            <img
              className="platform-title-icon"
              style={{
                height: '16px',
                width: '16px',
                margin: '0 4px',
                verticalAlign: 'middle',
              }}
              src={getDataSourceIcon(titleIcon)}
            />
            {sourceMap[titleIcon]}
            {data_source === 'ogdb' && ' Calibrated Data(DataBrain校准数据).'}
          </>
        }
        customActions={() => {
          // dispatch({
          //   type: 'topchartsState/setUrlParams',
          //   payload: {
          //     dataSourceArr: [isPcuOrAcu ? 'steamcharts' : titleIcon],
          //   },
          // });

          history.push({
            pathname: datamapIntelligenceMetricsPath,
            query: {
              pageid: data_source === 'newzoo' ? 'ogdb' : data_source,
            },
          });
        }}
        // isNoMore={data_source === 'gsd'}
      />
    );
  };

  const icon = data_source === 'ogdb' ? 'databrain' : titleIcon; // 卡片的数据源图标以及hover数据源提示，优先以summary接口的data_source字段为准（ogdb）

  const showMobileDataSourceRangeInfo =
    moduleName === 'mobileGames' &&
    ['Downloads', 'DAU', 'Revenue($)', 'RPD'].includes(titleContent);
  const handleClickTitle = () => {
    if (link_url) {
      history.push(link_url);
    }
  };
  return (
    <div className="context-title-flex">
      <div className="context-title">
        {isShowDataSourceIcon && (
          <>
            <Bubble
              className="dbi-bubble-auto"
              dark
              arrowPointAtCenter
              placement={index ? 'top' : 'top-start'}
              style={{ maxWidth: 500 }}
              content={renderDataSourceTips(icon, titleContent, data_source, metricTitleLocaleText)}
            >
              <img className="context-title-icon" src={getDataSourceIcon(icon)} alt="" />
            </Bubble>
            {getIsUsMetric(cardItem) && (
              <img className="context-title-iconcountry" src={getCountryFlag('us')} alt="" />
            )}
          </>
        )}
        {/* 标题文本 */}
        <AggregationBubble
          AWidth={aggregationBubbleWidth}
          showMobileDataSourceRangeInfo={showMobileDataSourceRangeInfo}
          DomOrText={
            <span className="title-content" onClick={handleClickTitle}>
              {metricTitleLocaleText}
            </span>
          }
          type={entityType}
          market={metricTitle}
          platform={data_source}
          cardItem={cardItem}
          customBubbleContent={
            metricTitle !== 'Steam PCU' ? (
              customBubbleContent
            ) : (
              <>
                <p>{formatMessage({ id: 'dbi_info_max_selected' })}</p>
                <p>
                  {formatMessage(
                    { id: 'dbi_common_data_from_tips' },
                    {
                      metric: metricTitleLocaleText,
                      dataSource: 'VG insights',
                    },
                  )}
                </p>
              </>
            )
          }
        />
        {isShowInfoIcon && tipsInfoText && (
          <span style={{ marginLeft: 6, display: 'inline-flex', alignItems: 'center' }}>
            <TipContext
              className="icon-top"
              tipText={tipsInfoText}
              AWidth={430}
            />
          </span>
        )}
        {metricTitleLocaleText && metricTitleLocaleText !== '' && (
          <SvgIcon className="context-title-arrow" type="arrow-right" />
        )}
        {/* {
          // 判断是否显示提示图标
          showTipList.includes(titleContent) ? (
            <TipContext
              AWidth={420}
              className="icon-top tip-text-hd"
            // tipText={DealContent(titleContent)}
            />
          ) : (
            ''
          )
        } */}
        {reginonOnlyTips ? (
          <Tooltip placement="top">
            <div className="context-title-date only-date">
              {titleContent === 'Digital ARPPU($)' ? 'US data only' : 'Global data only'}
            </div>
          </Tooltip>
        ) : (
          ''
        )}
      </div>

      {baseIndicators?.latestDate && baseIndicators?.latestDate !== 'Invalid date' ? (
        <div className="context-title-desc">{baseIndicators?.latestDate}</div>
      ) : (
        ''
      )}
    </div>
  );
};

export default CardTitleContent;
